import React, { useEffect, useState } from "react";

import { NavLink, useLocation, useNavigate } from "react-router-dom";

import MyCouponLogo from "../../../../assets/images/MyCouponLogo.svg";
import MyCouponLogoMobile from "../../../../assets/images/MyCouponLogoMobile.svg";
import ImpersonationSwitcher from "containers/Dashboard/Components/ImpersonationSwitcher";

// ─── Section map: which sections exist on each page ───────────────────────────
// NOTE: no longer used for header nav (replaced by NAV_LINKS below), kept
// exported in case other code still imports PAGE_SECTIONS.
// NOTE: keys below are still "/" and "/business" — this file's routes moved to
// "/landing" and "/landing/business", so SectionDotNav will currently render
// nothing on either page. Flagging again since it wasn't part of this change.
export const PAGE_SECTIONS: Record<string, { id: string; label: string }[]> = {
  "/": [
    { id: "hero", label: "Home" },
    { id: "smart-deals", label: "Smart Deals" },
    { id: "about", label: "About" },
    { id: "categories", label: "Categories" },
    { id: "personalised", label: "Personalised" },
    { id: "save-redeem", label: "Save & Redeem" },
    { id: "rewards", label: "Rewards" },
    { id: "premium", label: "Premium" },
    { id: "download", label: "Download" },
  ],
  "/business": [
    { id: "hero", label: "Hero" },
    { id: "stats", label: "Stats" },
    { id: "profile", label: "Profile" },
    { id: "why-coupons", label: "Why Coupons" },
    { id: "brands", label: "Brands" },
    { id: "deal-ideas", label: "Deal Ideas" },
    { id: "advantage", label: "Advantage" },
    { id: "community", label: "Community" },
    { id: "plans", label: "Plans" },
  ],
  "/page3": [
    { id: "pricing-hero", label: "Overview" },
    { id: "pricing-plans", label: "Plans" },
  ],
};

/* ── Nav icons — copied from CustomerHeader.js so the landing header's pill
   nav matches the app header's icon set exactly. Each accepts `active`: true
   renders filled (solid), false renders outline (stroke only). ── */

const HomeIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.15 1.86a1.35 1.35 0 0 1 1.7 0l6.53 5.28c.4.33.62.81.62 1.32v8.04c0 .94-.76 1.7-1.7 1.7h-4.15a.85.85 0 0 1-.85-.85v-4.5c0-.4-.05-.75-.13-.93a1.55 1.55 0 0 0-1.17-.94 1.9 1.9 0 0 0-.7 0 1.55 1.55 0 0 0-1.17.94c-.08.18-.13.53-.13.93v4.5c0 .47-.38.85-.85.85H3.3c-.94 0-1.7-.76-1.7-1.7V8.46c0-.51.22-.99.62-1.32l6.93-5.28Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.4}
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = ({ active }: { active: boolean }) => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle
      cx="8.5"
      cy="8.5"
      r="6.2"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.6}
    />
    <path d="M13.4 13.4 18 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BusinessesIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 8.2 3.6 2.6h12.8l1.1 5.6"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 8v8.6c0 .5.4.9.9.9h12.2c.5 0 .9-.4.9-.9V8"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 17.5V12h4v5.5"
      fill={active ? "#fff" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
  </svg>
);

const MapsIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 18.2s6.3-5.6 6.3-10.4a6.3 6.3 0 1 0-12.6 0c0 4.8 6.3 10.4 6.3 10.4Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
    <circle
      cx="10"
      cy="7.8"
      r="2.3"
      fill={active ? "#fff" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
    />
  </svg>
);

const BlogIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 4.2c0-.66.54-1.2 1.2-1.2h6.2c.66 0 1.2.54 1.2 1.2v11.6c0 .66-.54 1.2-1.2 1.2H4.2c-.66 0-1.2-.54-1.2-1.2V4.2Z"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
    <path
      d="M5.6 6.4h4.6M5.6 9h4.6M5.6 11.6h2.8"
      stroke={active ? "#fff" : "currentColor"}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M13.6 5.6 16.4 6.5c.62.2.96.87.76 1.49l-2.6 8c-.2.63-.87.97-1.49.77l-1.27-.41"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Customer/business shared nav links (replaces the old section quick-jump) ──
const NAV_LINKS = [
  { label: "Home", to: "/home", Icon: HomeIcon },
  { label: "Search", to: "/coupons-list", Icon: SearchIcon },
  { label: "Businesses", to: "/coupons-map", Icon: MapsIcon },
  { label: "Online Brands", to: "/online-brands-list", Icon: BusinessesIcon },
  { label: "Blog", to: "/blogs", Icon: BlogIcon },
];

// ── Sizing below mirrors CustomerHeader.module.scss's clamp() rules 1:1, so
// the landing header scales identically at wide viewports instead of sitting
// flat at small fixed sizes. ──

const linkPillClass = ({ isActive }: { isActive: boolean }) => `
  flex items-center gap-[clamp(6px,0.5vw,10px)] h-[clamp(36px,2.6vw,52px)]
  px-[clamp(22px,1.8vw,34px)] rounded-full whitespace-nowrap no-underline
  font-['Poppins',Helvetica] text-[clamp(13px,0.65vw+7px,18px)] font-bold
  transition-colors duration-150
  ${isActive
    ? "bg-gradient-to-br from-[#ef0023] to-[#d4001d] text-white shadow-[0_3px_10px_rgba(212,0,29,0.35)]"
    : "text-[#26262b] hover:bg-black/[0.06]"
  }
`;

const mobileLinkPillClass = ({ isActive }: { isActive: boolean }) => `
  flex items-center gap-2 px-3 py-2.5 rounded-[12px] text-left no-underline
  font-['Poppins',Helvetica] text-[13px] font-bold
  transition-colors duration-150
  ${isActive
    ? "bg-gradient-to-br from-[#ef0023] to-[#d4001d] text-white shadow-[0_3px_10px_rgba(212,0,29,0.35)]"
    : "text-[#26262b] bg-black/[0.04] hover:bg-black/[0.08]"
  }
`;

const secondaryBtnClass =
  "flex items-center gap-[clamp(4px,0.4vw,7px)] h-[clamp(40px,2.8vw,56px)] px-[clamp(16px,1.5vw,26px)] " +
  "rounded-full border border-black/[0.15] bg-transparent no-underline " +
  "font-['Poppins',Helvetica] text-[clamp(13px,0.65vw+7px,18px)] font-semibold text-[#26262b] whitespace-nowrap " +
  "transition-colors duration-150 hover:bg-black/5 hover:border-black/25";

const signinBtnClass =
  "flex items-center gap-[clamp(4px,0.4vw,7px)] h-[clamp(40px,2.8vw,56px)] px-[clamp(16px,1.5vw,26px)] " +
  "rounded-full border border-black/[0.15] bg-transparent no-underline " +
  "font-['Poppins',Helvetica] text-[clamp(13px,0.65vw+7px,18px)] font-semibold text-[#26262b] whitespace-nowrap " +
  "transition-colors duration-150 hover:bg-gradient-to-br hover:from-[#ef0023] hover:to-[#d4001d] " +
  "hover:border-transparent hover:text-white";

const primaryBtnClass =
  "flex items-center gap-[clamp(4px,0.4vw,7px)] h-[clamp(40px,2.8vw,56px)] px-[clamp(16px,1.5vw,26px)] " +
  "rounded-full bg-gradient-to-br from-[#ef0023] to-[#d4001d] no-underline " +
  "font-['Poppins',Helvetica] text-[clamp(13px,0.65vw+7px,18px)] font-semibold text-white whitespace-nowrap " +
  "transition-opacity duration-150 hover:opacity-90";

const secondaryBtnClassMobile =
  "flex-1 flex items-center justify-center h-11 px-5 rounded-full border border-black/[0.15] bg-transparent no-underline " +
  "font-['Poppins',Helvetica] text-[14px] font-semibold text-[#26262b] " +
  "transition-colors duration-150 hover:bg-black/5 hover:border-black/25";

const primaryBtnClassMobile =
  "flex-1 flex items-center justify-center h-11 px-5 rounded-full bg-gradient-to-br from-[#ef0023] to-[#d4001d] no-underline " +
  "font-['Poppins',Helvetica] text-[14px] font-semibold text-white";

export const HeaderWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isBusinessPage = location.pathname === "/landing/business";

  // Reset scroll position on route change
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${scrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_2px_24px_rgba(0,0,0,0.08)]"
            : "bg-white/80 backdrop-blur-sm"
          }
        `}
      >
        <div className="max-w-[1820px] mx-auto px-6 lg:px-[clamp(32px,2.6vw,60px)] py-[clamp(10px,0.8vw,18px)] flex items-center gap-[clamp(16px,1.1vw,26px)]">

          {/* ── LOGO ── */}
          <a href="/landing" className="flex items-center shrink-0 mr-2 min-w-0 max-w-[clamp(250px,15vw,340px)]">
            <img
              src={MyCouponLogo}
              alt="MyCoupon"
              className="hidden lg:block max-h-[clamp(56px,3.4vw,78px)] max-w-full w-auto h-auto object-contain"
            />
            <img
              src={MyCouponLogoMobile}
              alt="MyCoupon"
              className="lg:hidden max-h-10 max-w-full w-auto h-auto object-contain"
            />
          </a>

          {/* ── SHARED NAV LINKS (Home / Search / Businesses / Maps / Blog) ── */}
          <div className="hidden xl:flex items-center gap-[clamp(6px,0.5vw,12px)] h-[clamp(48px,3.2vw,68px)] px-[clamp(8px,0.7vw,14px)] rounded-full bg-black/[0.07] flex-1 min-w-0 max-w-fit">
            {NAV_LINKS.map(({ label, to, Icon }) => (
              <NavLink key={to} to={to} className={linkPillClass}>
                {({ isActive }) => (
                  <>
                    <Icon active={isActive} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* Spacer to push CTA right on smaller breakpoints where the row above is hidden */}
          <div className="flex-1 xl:hidden" />

          {/* ── CTA ── */}
          {isBusinessPage ? (
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              <button onClick={() => navigate("/landing")} className={secondaryBtnClass}>
                Join as a Customer
              </button>
              <a href="https://mycouponbook.ca/login" className={signinBtnClass}>
                Sign In
              </a>
              <a href="https://mycouponbook.ca/signup" className={primaryBtnClass}>
                Sign Up
              </a>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              <button onClick={() => navigate("/")} className={secondaryBtnClass}>
                Browse Deals!
              </button>
              <button onClick={() => navigate("/landing/business")} className={primaryBtnClass}>
                Join as a Business
              </button>
            </div>
          )}

          {/* ── MOBILE HAMBURGER ── */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-[5px] rounded-full border border-black/[0.15] hover:bg-black/5 transition"
            aria-label="Toggle menu"
          >
            <span
              className={`block w-5 h-[2px] bg-neutral-800 rounded-full transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-[7px]" : ""}`}
            />
            <span
              className={`block w-5 h-[2px] bg-neutral-800 rounded-full transition-all duration-300 ${mobileOpen ? "opacity-0" : ""}`}
            />
            <span
              className={`block w-5 h-[2px] bg-neutral-800 rounded-full transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-[7px]" : ""}`}
            />
          </button>
        </div>

        {/* ── MOBILE MENU ── */}
        <div
          className={`
            lg:hidden overflow-hidden transition-all duration-300
            ${mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}
            bg-white border-t border-neutral-100
          `}
        >
          <div className="px-6 py-4 flex flex-col gap-1">

            {/* Shared nav links (Home / Search / Businesses / Maps / Blog) */}
            <p className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-2 mt-1">
              Explore
            </p>
            <div className="grid grid-cols-2 gap-2">
              {NAV_LINKS.map(({ label, to, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={mobileLinkPillClass}
                >
                  {({ isActive }) => (
                    <>
                      <Icon active={isActive} />
                      {label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>

            {isBusinessPage ? (
              <div className="mt-4 flex flex-col gap-2">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate("/landing");
                  }}
                  className={secondaryBtnClassMobile}
                >
                  Join as a Customer
                </button>
                <div className="flex gap-2">
                  
                 <a href="https://mycouponbook.ca/login"
                    className={secondaryBtnClassMobile}
                  >
                    Sign In
                  </a>
                  
                  <a  href="https://mycouponbook.ca/signup"
                    className={primaryBtnClassMobile}
                  >
                    Sign Up
                  </a>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate("/");
                  }}
                  className={secondaryBtnClassMobile}
                >
                  Browse Deals!
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    navigate("/landing/business");
                  }}
                  className={primaryBtnClassMobile}
                >
                  Join as a Business
                </button>
              </div>
            )}
          </div>
          
        </div>
        
      </header>
    </>
  );
};